import type { LocaleCode } from '@/i18n/locales'
import { extractLanguage } from '@/voice/nlu/extract'
import { words } from '@/voice/nlu/normalize'

/**
 * Reading the language out of the first reply.
 *
 * Namu greets in both languages and the language of the reply becomes the
 * language. `extractLanguage` already reads somebody who names a language
 * outright, and it is asked first, but almost nobody answers "Sannu. Hello."
 * by saying the word "Hausa". They say "Sannu", or "ina kwana", or they ask
 * their question straight away. So the language of the words themselves has to
 * be read, and that is what this adds.
 *
 * The alternative was to show the menu after all: greet in both, and if the
 * reply is not a language name, ask "Hausa or English?". That is the form this
 * design exists to avoid, and it is worse than a wrong guess, because a wrong
 * guess is fixed by one sentence at any later point while a menu is paid for
 * by everybody at the first moment.
 *
 * Detection is a weighted marker count rather than an n-gram model. Two
 * reasons, and both are about this product rather than about accuracy in
 * general: it has to run offline on a cheap phone before the first word is
 * spoken, and when it gets something wrong the reason has to be readable in
 * one file rather than being a number in a matrix.
 */

export type LocaleBasis =
  /** The person named a language: "Hausa", "in English". */
  | 'named'
  /** Read from the words of the reply itself. */
  | 'spoken'
  /** Nothing usable was said, so the device's own language stands. */
  | 'device'

export interface LocaleReading {
  locale: LocaleCode
  basis: LocaleBasis
  /** 0..1. Low on the device fallback, which is a default rather than a reading. */
  confidence: number
}

/**
 * The hooked letters ɓ ɗ ƙ ƴ are distinct letters of Hausa and appear in no
 * English word, so one of them settles the question on its own. `normalize`
 * deliberately preserves them, which is what makes this possible.
 */
const HAUSA_LETTERS = /[ɓɗƙƴ]/u

/**
 * Words that occur in one language and not the other.
 *
 * The tiers matter more than the lists. A strong marker is a word that decides
 * the question by itself; a weak one only counts alongside others, because it
 * is short, or common in transcription noise, or a plausible mishearing of
 * something in the other language.
 *
 * What is deliberately absent is anything a recogniser could produce for
 * either language. "in", "a", "i", "to", "me", "so", "an" and "on" are all
 * ordinary English words and all also occur in Hausa, and every one of them
 * appears in the sort of two-word reply this has to read. Including them made
 * "I'm in Kano" score as Hausa. `script.test.ts` asserts the two vocabularies
 * stay disjoint, because a word added to one list months from now will not be
 * checked against the other by eye.
 */
const HAUSA_STRONG = [
  'sannu',
  'barka',
  'kwana',
  'lafiya',
  'yaya',
  'ina',
  'kana',
  'kina',
  'kake',
  'kike',
  'nake',
  'muna',
  'suna',
  'yana',
  'tana',
  'gobe',
  'yau',
  'yanzu',
  'gaskiya',
  'kuma',
  'amma',
  'wannan',
  'yaushe',
  'wane',
  'wace',
  'nawa',
  'zan',
  'zaka',
  'abin',
  'sosai',
  'kaɗan',
  'kadan',
  'aiki',
  'magana',
  'taimaka',
  'taimake',
  'tambaya',
  'sunana',
  'mace',
  'namiji',
  'gida',
  'kasuwa',
  'asibiti',
  'ruwa',
  'gari',
  'birni',
  'iyali',
  'malam',
  'madalla',
  'tsallake',
  'hausa',
  'hausanci',
  'toh',
  "a'a",
]

const HAUSA_WEAK = ['ne', 'ce', 'ba', 'ee', 'ka', 'ki', 'ni', 'na', 'ta', 'ya', 'da', 'mu', 'ku', 'shi', 'ita', 'sai', 'har', 'ko', 'dai', 'don', 'wa']

const ENGLISH_STRONG = [
  'the',
  'is',
  'are',
  'was',
  'were',
  'what',
  'where',
  'when',
  'why',
  'how',
  'hello',
  'good',
  'morning',
  'evening',
  'please',
  'can',
  'could',
  'you',
  'your',
  'and',
  'with',
  'want',
  'need',
  'help',
  'tell',
  'about',
  'that',
  'this',
  'for',
  'does',
  'have',
  'there',
  'here',
  'from',
  'they',
  'know',
  'think',
  'like',
  'going',
  'would',
  'should',
  'thanks',
  'thank',
  'sorry',
  'name',
  'price',
  'market',
  'hospital',
  'today',
  'tomorrow',
  'again',
  'english',
]

/**
 * "Yes", "no" and "okay" are weak on purpose despite being unambiguously
 * English words. Code-switching into English for the small change of a
 * sentence is ordinary across northern Nigeria, and a Hausa speaker who
 * answers "yes" has not asked to be spoken to in English.
 */
const ENGLISH_WEAK = ['hi', 'my', 'do', 'it', 'of', 'has', 'she', 'he', 'we', 'get', 'yes', 'no', 'okay', 'sure', 'right']

const STRONG_WEIGHT = 3
const WEAK_WEIGHT = 1

/**
 * How far ahead one language has to be before its lead means anything.
 *
 * Three is one strong marker, or three weak ones. Below that the reply is
 * something like "Kano", which is a word in both languages and in neither, and
 * guessing from it is worse than keeping the device's own language: a wrong
 * guess makes Namu speak the wrong language to somebody who has said almost
 * nothing, which is the moment they are least able to correct it.
 */
const DECISIVE_LEAD = 3

const VOCABULARIES: Record<LocaleCode, { strong: ReadonlySet<string>; weak: ReadonlySet<string> }> =
  {
    ha: { strong: new Set(HAUSA_STRONG), weak: new Set(HAUSA_WEAK) },
    en: { strong: new Set(ENGLISH_STRONG), weak: new Set(ENGLISH_WEAK) },
  }

/** Exported so the disjointness of the two vocabularies can be asserted. */
export const LOCALE_MARKERS = VOCABULARIES

function score(tokens: readonly string[], locale: LocaleCode): number {
  const { strong, weak } = VOCABULARIES[locale]
  let total = 0
  for (const token of tokens) {
    if (strong.has(token)) total += STRONG_WEIGHT
    else if (weak.has(token)) total += WEAK_WEIGHT
  }
  return total
}

/**
 * Decides the conversation's language from the first thing somebody says.
 *
 * `deviceLocale` is whatever the app is already in, which on a first run is
 * Hausa and after that is whatever the person settled on. It is the answer for
 * silence and nothing more. It is emphatically not read from the phone: a
 * handset in Kano or Maradi is very often a Hausa speaker's phone that was
 * sold to them in English, and that is exactly the person this product is
 * for. `src/i18n/locales.ts` is where that stopped being a guess.
 */
export function readReplyLocale(transcript: string, deviceLocale: LocaleCode): LocaleReading {
  const named = extractLanguage(transcript)
  if (named.value !== null) {
    return { locale: named.value, basis: 'named', confidence: named.confidence }
  }

  if (HAUSA_LETTERS.test(transcript)) {
    return { locale: 'ha', basis: 'spoken', confidence: 0.95 }
  }

  const tokens = words(transcript)
  if (tokens.length === 0) {
    return { locale: deviceLocale, basis: 'device', confidence: 0.3 }
  }

  const hausa = score(tokens, 'ha')
  const english = score(tokens, 'en')
  const lead = Math.abs(hausa - english)
  if (lead < DECISIVE_LEAD) {
    return { locale: deviceLocale, basis: 'device', confidence: 0.3 }
  }

  return {
    locale: hausa > english ? 'ha' : 'en',
    basis: 'spoken',
    // Caps out well short of certainty. This is a guess from a handful of
    // words and the assistant should stay willing to be corrected by the next
    // sentence, which `extractLanguage` in the open conversation already does.
    confidence: Math.min(0.9, 0.55 + lead * 0.05),
  }
}
